import * as amplitude from '@amplitude/analytics-browser';
import { defaultEventTrackingAdvancedPlugin } from '@amplitude/plugin-default-event-tracking-advanced-browser';
import '@workday/canvas-tokens-web/css/base/_variables.css';
import '@workday/canvas-tokens-web/css/brand/_variables.css';
import '@workday/canvas-tokens-web/css/system/_variables.css';
import 'react-datepicker/dist/react-datepicker.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import 'react-sortable-tree/style.css';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from 'theme-ui';

import { fetchCsrfToken } from '~/api/csrf';
import AuthenticationStore from '~/auth/authenticationStore';
import { ConditionalWrap } from '~/common/components/ConditionalWrap/ConditionalWrap';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { isDev } from '~/dev/utils/isDev';
import { FlagsProvider } from '~/flags/FlagsProvider';
import { BootMark, markBoot, markBootOnce } from '~/utils/bootTiming';
import { startTrackingPageTeardown } from '~/utils/pageTeardown';
import { scheduleWhenIdle } from '~/utils/scheduleWhenIdle';
import {
  recordSuppressedError,
  SuppressedErrorReason,
} from '~/utils/suppressedErrorTelemetry';

import '../packages/eds/src/index';
import { startAssetFailureReporting } from './bootstrap/assetFailureReporting';
import { startChunkLoadRecovery } from './bootstrap/chunkLoadRecovery';
import { initSentry } from './bootstrap/sentry';
import App from './components/App/App';
import store, { persistor } from './store';
import './styles/index.css';
import './styles/normalize.css';
import './styles/react-ckeditor_overrides.css';
import './styles/react-datepicker_overrides.css';
import './styles/react-pdf_overrides.css';
import './styles/react-tooltip_overrides.css';
import './styles/react_dates_overrides.css';
import theme from './ui/theme';

if (!isDev) {
  amplitude.init(
    getEnvVariable('REACT_APP_AMPLITUDE_API_KEY') ?? '',
    undefined,
    {
      defaultTracking: {
        pageViews: false,
      },
    },
  );
  amplitude.add(defaultEventTrackingAdvancedPlugin());
}

startTrackingPageTeardown();
startChunkLoadRecovery();
startAssetFailureReporting();
initSentry();

// The axios request interceptor lazily awaits the CSRF token before the first
// request of the session (see `src/services/createService.ts`), which
// serializes every other request behind that round trip. Starting it here lets
// it overlap module download and React's first render instead. `fetchCsrfToken`
// dedupes while pending, so the interceptor's `await` resolves immediately once
// this settles. Errors are ignored on purpose: the interceptor retries, and a
// failure here must not block bootstrap.
if (AuthenticationStore().isLoggedIn()) {
  markBoot(BootMark.CsrfFetchStart);
  void fetchCsrfToken()
    .catch(() => {})
    .finally(() =>
      markBootOnce(BootMark.CsrfFetchEnd, BootMark.CsrfFetchStart),
    );
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to bootstrap: #root element missing from DOM.');
}

// Session replay and profiling are registered after boot so their code lands in
// a lazy chunk and their setup does not compete with first render. See
// ./bootstrap/sentryDeferredIntegrations for the observability tradeoff.
//
// The catch matters: a boot-time auth redirect often aborts this import, and
// the unhandled rejection was being reported as a missing chunk.
scheduleWhenIdle(() => {
  void import('./bootstrap/sentryDeferredIntegrations')
    .then(({ addDeferredSentryIntegrations }) =>
      addDeferredSentryIntegrations(),
    )
    .catch((error: unknown) => {
      recordSuppressedError(SuppressedErrorReason.DeferredIntegrationsImport, {
        message: error instanceof Error ? error.message : String(error),
      });
    });
});

createRoot(container).render(
  <Provider store={store}>
    {/* PersistGate only gates rendering in local dev, where the persistor
        exists; in every other environment the app renders immediately. */}
    <ConditionalWrap
      condition={Boolean(persistor)}
      wrapper={(children) => (
        <PersistGate persistor={persistor!}>{children}</PersistGate>
      )}
    >
      <ThemeProvider theme={theme}>
        <FlagsProvider>
          <App />
        </FlagsProvider>
      </ThemeProvider>
    </ConditionalWrap>
  </Provider>,
);
